import React, { useState, useEffect, useRef } from "react";
import { FileText, Mail, Plus, Trash2, X, Eye, EyeOff, Lock, CornerDownRight, ClipboardList, Layers, Pencil, ArrowUp, ArrowDown, RotateCcw, Check, MoreVertical } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bg, Bt, Cd, Sec, Sl, Inp, Tbl, KpiStrip } from "../components";
import { notify, confirmDialog } from "../notify";
import { RichTextEditor } from "../components/RichTextEditor";
import { TEMPLATE_KINDS, MERGE_FIELDS } from "../templateConstants";
import { useFormCatalogue, invalidateForms, loadForms } from "../formCatalog";
import { FormRenderer } from "../components/FormRenderer";
import { fmtDMY } from "../dateFormat";

// ─── Email / Form Templates ────────────────────────────────────────────────
// Two tabs: Email Template is the reusable rich-text email library; Form
// Template is the catalogue of forms this HRMS runs, plus any custom form
// templates. The forms come from /api/form-templates — the definition the
// live forms draw themselves from — so previewing shows exactly what people
// fill in, and editing one here changes it for them.

const KIND_ICON = { email: Mail, form: ClipboardList };
const fmtDate = (d) => d ? fmtDMY(d) : "—";

// Same backdrop/card/animation pair as every other dialog in the app.
const Modal = ({ title, subtitle, width = 640, onClose, children }) => (
  <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div
      className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-h-[88vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
      style={{ maxWidth: width }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-start gap-3 px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
        <div className="min-w-0">
          <div className="text-base font-extrabold text-t0 truncate">{title}</div>
          {subtitle && <div className="text-[14px] text-t3 mt-[2px]">{subtitle}</div>}
        </div>
        <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1 flex-shrink-0"><X size={18} /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const EmptyState = ({ icon: Icon, title, subtitle, ctaLabel, onCta }) => (
  <div className="text-center py-14 px-6">
    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: K.pg, border: `1px solid ${K.pb}` }}>
      <Icon size={24} color={K.p} />
    </div>
    <div className="text-[15px] font-bold text-t0 mb-1">{title}</div>
    <div className="text-[14px] text-t3 max-w-[420px] mx-auto mb-4">{subtitle}</div>
    {ctaLabel && <Bt icon={Plus} onClick={onCta}>{ctaLabel}</Bt>}
  </div>
);

// ─── Template editor ──────────────────────────────────────────────────────
const TemplateEditDialog = ({ template, kind, forms, onClose, onSaved }) => {
  const [name, setName] = useState(template?.name || "");
  const [formKey, setFormKey] = useState(template?.form_key || forms?.[0]?.key || "employee");
  const [subject, setSubject] = useState(template?.subject || "");
  const [body, setBody] = useState(template?.body || "");
  const [defaultCc, setDefaultCc] = useState((template?.default_cc || []).join(", "));
  const [saving, setSaving] = useState(false);
  const kindLabel = TEMPLATE_KINDS.find((k) => k.v === kind)?.l || "Template";

  const save = async () => {
    if (!name.trim() || !body.trim()) { notify("Name and body are required", { title: "Error", variant: "error" }); return; }
    if (kind === "email" && !subject.trim()) { notify("Subject is required for an email template", { title: "Error", variant: "error" }); return; }
    setSaving(true);
    try {
      const payload = { name, subject, body, default_cc: defaultCc.split(",").map((s) => s.trim()).filter(Boolean), kind, form_key: formKey };
      if (template?.id) await api.put(`/api/templates/${template.id}`, payload);
      else await api.post("/api/templates", payload);
      notify("Template saved", { title: "Saved", variant: "success" });
      onSaved();
    } catch (e) { notify(e.message || "Failed to save", { title: "Error", variant: "error" }); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      title={`${template?.id ? "Edit" : "New"} ${kindLabel}`}
      subtitle={kind === "email" ? "Reusable message — merge fields fill in per employee." : "Reusable form content — merge fields fill in per record."}
      onClose={onClose}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-3">
        <div className={kind === "email" ? "" : "sm:col-span-2"}>
          <div className="text-[14px] text-t2 mb-[3px]">Template name <span style={{ color: K.r }}>*</span></div>
          <Inp value={name} onChange={setName} placeholder={kind === "email" ? "e.g. Offer follow-up" : "e.g. Exit interview questions"} />
        </div>
        {kind === "email" && (
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">Subject <span style={{ color: K.r }}>*</span></div>
            <Inp value={subject} onChange={setSubject} placeholder="Hi ${name}, …" />
          </div>
        )}
        {forms?.length > 0 && (
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">Record type</div>
            <Sl options={forms.map((f) => ({ v: f.key, l: f.label }))} value={formKey} onChange={setFormKey} />
          </div>
        )}
        <div>
          <div className="text-[14px] text-t2 mb-[3px]">Default CC</div>
          <Inp value={defaultCc} onChange={setDefaultCc} placeholder="hr@company.com, ops@company.com" />
        </div>
      </div>

      <div className="text-[14px] text-t2 mb-[3px]">Message</div>
      <RichTextEditor value={body} onChange={setBody} mergeFields={MERGE_FIELDS[formKey] || []} />

      <div className="flex gap-[6px] mt-4">
        <Bt full disabled={saving} onClick={save}>{saving ? "Saving…" : "Save Template"}</Bt>
        <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
      </div>
    </Modal>
  );
};

// ─── Form catalogue ───────────────────────────────────────────────────────
// Type names as the editor labels them.
const FIELD_TYPE_LABEL = {
  read: "Auto-filled", rating: "Rating", select: "Dropdown", text: "Text",
  textarea: "Paragraph", date: "Date", number: "Number", file: "File upload",
  checkbox: "Tick box", signature: "Signature",
};

const FormPreviewModal = ({ form, onClose }) => (
  <Modal title={form.name} subtitle={form.summary} width={760} onClose={onClose}>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-[10px] mb-4">
      {[["Filled by", form.filledBy], ["Where it opens", form.openedFrom], ["How it starts", form.createdBy]].map(([l, v]) => (
        <div key={l} className="bg-bg2 border border-b0 rounded-xl p-3">
          <div className="text-[11px] font-semibold text-t2 uppercase tracking-wider mb-[3px]">{l}</div>
          <div className="text-[14px] text-t0">{v}</div>
        </div>
      ))}
    </div>

    {form.twoPart ? (
      // Each part under its own banner — different people, different stages.
      form.sections.map((sec, i) => {
        const partStarts = i === 0 || form.sections[i - 1].part !== sec.part;
        return (
          <div key={sec.id}>
            {partStarts && (
              <div className="flex items-center gap-2 flex-wrap mt-4 mb-2">
                <Bg c={sec.part === 1 ? K.p : K.pu} sm>Part {sec.part}</Bg>
                {sec.hiddenFromEmployee
                  ? <span className="text-[12px] text-t3 inline-flex items-center gap-1"><Lock size={10} /> Hidden from the employee</span>
                  : <span className="text-[12px] text-t3 inline-flex items-center gap-1"><Eye size={10} /> Visible to the employee</span>}
              </div>
            )}
            <Cd style={{ marginBottom: 12, padding: 18 }}>
              <FormRenderer form={{ ...form, sections: [sec] }} readOnly showName={false} />
            </Cd>
          </div>
        );
      })
    ) : (
      <Cd style={{ marginBottom: 12, padding: 18 }}>
        <FormRenderer form={form} readOnly showName={false} />
      </Cd>
    )}

    <div className="text-[12px] text-t3">
      {form.statutory
        ? "Exactly what the person filling it sees — reproduced as the statutory form is issued, so its wording, field order and numbering can't be edited here."
        : "Exactly what the person filling it sees. Use Edit to rename fields, mark them required, hide the ones you don't need, reorder them, or change a dropdown's options."}
    </div>
  </Modal>
);


// Row actions behind a three-dot button. Closes on outside click and Escape.
const CardMenu = ({ items }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    // Capture phase so a modal's own stopPropagation() on mousedown (used
    // to stop backdrop-click-closes-modal) can't swallow this before it
    // reaches document — see SearchSelect/MultiSelect for the full story.
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown, true); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`w-8 h-8 rounded-[10px] border flex items-center justify-center cursor-pointer transition-colors ${open ? "text-p" : "text-t3 hover:text-p"}`}
        style={{ background: open ? K.pg : "transparent", borderColor: open ? K.pb : "transparent" }}
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div role="menu" className="animate-dropIn absolute top-[calc(100%+6px)] right-0 bg-bg1 border border-b1 rounded-xl p-[6px] w-[176px] shadow-[0_12px_36px_rgba(0,0,0,0.18)] z-20">
          {items.map((it) => (
            <button
              key={it.label}
              role="menuitem"
              onClick={() => { setOpen(false); it.onClick(); }}
              className="w-full flex items-center gap-2 px-[10px] py-[8px] rounded-[8px] border-0 bg-transparent text-t1 text-[14px] font-semibold cursor-pointer hover:bg-bg2 text-left"
            >
              <it.icon size={14} /> {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FormsCatalogue = () => {
  const { forms, loading, error, reload } = useFormCatalogue();
  const [preview, setPreview] = useState(null);
  const [edit, setEdit] = useState(null);

  return (
    <div className="mb-5">
      <Sec icon={Layers} title="Forms in use" />
      <div className="text-[14px] text-t3 -mt-[6px] mb-3">
        The review forms this HRMS runs. Preview one to see every field, or edit it to change what the people filling it in see.
      </div>

      {error && <Cd style={{ marginBottom: 12 }}><div className="text-[14px] text-r">{error}</div></Cd>}
      {loading && !forms.length ? (
        <Cd><div className="text-center py-10 text-t3 text-[14px]">Loading forms…</div></Cd>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {forms.map((f) => {
            const all = f.sections.reduce((n, s2) => n + s2.fields.length, 0);
            const hidden = f.sections.reduce((n, s2) => n + s2.fields.filter((x) => x.hidden).length, 0);
            return (
              <Cd key={f.key} style={{ padding: 18 }}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: K.pg, border: `1px solid ${K.pb}` }}>
                    <FileText size={18} color={K.p} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[15px] font-bold text-t0">{f.name}</span>
                      {f.twoPart && <Bg c={K.pu} sm>2 parts</Bg>}
                      {f.customized && <Bg c={K.a} sm>Edited</Bg>}
                      {f.statutory && <Bg c={K.g} sm>Statutory</Bg>}
                      {f.notLive && <Bg c={K.t3} sm>Not wired up</Bg>}
                    </div>
                    <div className="text-[14px] text-t3 mt-1">{f.summary}</div>
                    <div className="flex items-center gap-[10px] flex-wrap mt-[10px] text-[12px] text-t3">
                      <span className="inline-flex items-center gap-1"><CornerDownRight size={11} />{f.openedFrom}</span>
                      <span className="inline-flex items-center gap-1"><Eye size={11} />{all - hidden} of {all} fields shown</span>
                    </div>
                  </div>
                  <CardMenu items={f.statutory
                    ? [{ label: "Preview", icon: Eye, onClick: () => setPreview(f) }]
                    : [
                        { label: "Preview", icon: Eye, onClick: () => setPreview(f) },
                        { label: "Edit", icon: Pencil, onClick: () => setEdit(f) },
                      ]} />
                </div>
              </Cd>
            );
          })}
        </div>
      )}

      {preview && <FormPreviewModal form={preview} onClose={() => setPreview(null)} />}
      {edit && (
        <FormEditModal
          form={edit}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); invalidateForms(); reload(); }}
        />
      )}
    </div>
  );
};

// ─── Form editor ──────────────────────────────────────────────────────────
// Labels, help notes, required flags, visibility, field order, dropdown
// options and section titles. Structure isn't editable, and fields the
// server needs to accept a submit are locked.
const OptionsEditor = ({ value = [], onChange }) => {
  const [draft, setDraft] = useState("");
  return (
    <div className="mt-2">
      <div className="text-[12px] text-t3 uppercase tracking-wide mb-1">Dropdown options</div>
      <div className="flex flex-wrap gap-[6px] mb-2">
        {value.map((o, i) => (
          <span key={`${o}-${i}`} className="inline-flex items-center gap-1 text-[12px] px-[9px] py-[3px] rounded-pill border" style={{ background: K.bg2, borderColor: K.b0, color: K.t1 }}>
            {o}
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="bg-transparent border-0 cursor-pointer text-t3 p-0 leading-none" title="Remove"><X size={11} /></button>
          </span>
        ))}
        {!value.length && <span className="text-[12px] text-t3">No options yet.</span>}
      </div>
      <div className="flex gap-[6px]">
        <Inp value={draft} onChange={setDraft} placeholder="Add an option…" />
        <Bt v="secondary" s="sm" icon={Check} onClick={() => { const t = draft.trim(); if (!t) return; onChange([...value, t]); setDraft(""); }}>Add</Bt>
      </div>
    </div>
  );
};

const FieldRow = ({ field, index, count, onChange, onMove }) => {
  const [open, setOpen] = useState(false);
  const isDropdown = field.type === "select" || field.type === "rating";
  return (
    <div className={`rounded-xl border p-3 ${field.hidden ? "opacity-60" : ""}`} style={{ borderColor: K.b0, background: K.bg1 }}>
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex flex-col gap-[2px] pt-1">
          <button disabled={index === 0} onClick={() => onMove(index, -1)} className={`bg-transparent border-0 p-0 leading-none ${index === 0 ? "opacity-25 cursor-not-allowed" : "cursor-pointer text-t3 hover:text-p"}`} title="Move up"><ArrowUp size={13} /></button>
          <button disabled={index === count - 1} onClick={() => onMove(index, 1)} className={`bg-transparent border-0 p-0 leading-none ${index === count - 1 ? "opacity-25 cursor-not-allowed" : "cursor-pointer text-t3 hover:text-p"}`} title="Move down"><ArrowDown size={13} /></button>
        </div>

        <div className="flex-1 min-w-[200px]">
          <Inp value={field.label} onChange={(v) => onChange({ ...field, label: v })} />
          <div className="flex items-center gap-2 flex-wrap mt-[6px] text-[12px] text-t3">
            <Bg c={K.bl} sm>{FIELD_TYPE_LABEL[field.type] || field.type}</Bg>
            {field.locked && <span className="inline-flex items-center gap-1"><Lock size={10} /> required by the system</span>}
            {field.restricted && <span className="inline-flex items-center gap-1"><Lock size={10} /> {field.restricted}</span>}
            <button onClick={() => setOpen((o) => !o)} className="bg-transparent border-0 p-0 cursor-pointer text-p font-semibold">{open ? "Less" : "More"}</button>
          </div>
        </div>

        <div className="flex items-center gap-[6px] flex-wrap">
          <button
            onClick={() => !field.locked && onChange({ ...field, required: !field.required })}
            disabled={field.locked}
            className={`text-[12px] font-semibold px-[9px] py-[4px] rounded-pill border ${field.locked ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
            style={field.required ? { background: `${K.p}0d`, color: K.p, borderColor: `${K.p}33` } : { background: K.bg2, color: K.t3, borderColor: K.b0 }}
          >
            Required
          </button>
          <button
            onClick={() => !field.locked && onChange({ ...field, hidden: !field.hidden })}
            disabled={field.locked}
            className={`inline-flex items-center gap-1 text-[12px] font-semibold px-[9px] py-[4px] rounded-pill border ${field.locked ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
            style={field.hidden ? { background: `${K.a}0d`, color: K.a, borderColor: `${K.a}33` } : { background: K.bg2, color: K.t3, borderColor: K.b0 }}
          >
            {field.hidden ? <><EyeOff size={11} /> Hidden</> : <><Eye size={11} /> Shown</>}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: K.b0 }}>
          <div className="text-[12px] text-t3 uppercase tracking-wide mb-1">Help text under the field</div>
          <Inp value={field.note || ""} onChange={(v) => onChange({ ...field, note: v })} placeholder="Optional guidance for whoever fills this in" />
          {isDropdown && <OptionsEditor value={field.options || []} onChange={(options) => onChange({ ...field, options })} />}
        </div>
      )}
    </div>
  );
};

const FormEditModal = ({ form, onClose, onSaved }) => {
  const [sections, setSections] = useState(() => JSON.parse(JSON.stringify(form.sections)));
  const [saving, setSaving] = useState(false);

  const patchSection = (idx, patch) => setSections((prev) => prev.map((s2, i) => (i === idx ? { ...s2, ...patch } : s2)));
  const patchField = (si, fi, field) => setSections((prev) => prev.map((s2, i) =>
    i === si ? { ...s2, fields: s2.fields.map((f, j) => (j === fi ? field : f)) } : s2));
  const moveField = (si, fi, dir) => setSections((prev) => prev.map((s2, i) => {
    if (i !== si) return s2;
    const fields = [...s2.fields];
    const to = fi + dir;
    if (to < 0 || to >= fields.length) return s2;
    [fields[fi], fields[to]] = [fields[to], fields[fi]];
    return { ...s2, fields };
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/form-templates/${form.key}`, { sections });
      notify("Form updated — everyone filling it in sees the change", { title: "Saved", variant: "success" });
      onSaved();
    } catch (e) { notify(e.message || "Failed to save", { title: "Error", variant: "error" }); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!(await confirmDialog(`Put "${form.name}" back to its built-in layout? Your changes to it are discarded.`))) return;
    setSaving(true);
    try {
      await api.post(`/api/form-templates/${form.key}/reset`, {});
      notify("Form reset to its built-in layout", { title: "Reset", variant: "success" });
      onSaved();
    } catch (e) { notify(e.message || "Failed to reset", { title: "Error", variant: "error" }); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Edit ${form.name}`} subtitle="Rename fields, mark them required, hide what you don't need, reorder them, or change a dropdown's options." width={860} onClose={onClose}>
      {sections.map((sec, si) => (
        <div key={sec.id} className="mb-4">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {form.twoPart && <Bg c={sec.part === 1 ? K.p : K.pu} sm>Part {sec.part}</Bg>}
            {sec.hiddenFromEmployee && <span className="text-[12px] text-t3 inline-flex items-center gap-1"><Lock size={10} /> Hidden from the employee</span>}
          </div>
          <div className="mb-2">
            <div className="text-[12px] text-t3 uppercase tracking-wide mb-1">Section title</div>
            <Inp value={sec.title} onChange={(v) => patchSection(si, { title: v })} />
          </div>
          <div className="grid gap-[8px]">
            {sec.fields.map((f, fi) => (
              <FieldRow
                key={f.id}
                field={f}
                index={fi}
                count={sec.fields.length}
                onChange={(next) => patchField(si, fi, next)}
                onMove={(i, dir) => moveField(si, i, dir)}
              />
            ))}
          </div>
        </div>
      ))}

      <div className="flex gap-[6px] flex-wrap sticky bottom-0 bg-bg1 pt-3 border-t" style={{ borderColor: K.b0 }}>
        <Bt disabled={saving} onClick={save}>{saving ? "Saving…" : "Save Changes"}</Bt>
        <Bt v="secondary" onClick={onClose}>Cancel</Bt>
        <div className="flex-1" />
        {form.customized && <Bt v="ghost" icon={RotateCcw} disabled={saving} onClick={reset}>Reset to built-in</Bt>}
      </div>
    </Modal>
  );
};

// ─── Saved template library (both kinds) ──────────────────────────────────
const TemplatesLibraryPanel = ({ kind, forms, editing, setEditing, onCountChange }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const kindLabel = TEMPLATE_KINDS.find((k) => k.v === kind)?.l || "Template";
  const isForm = kind === "form";

  const load = () => {
    setLoading(true);
    api.get(`/api/templates?kind=${kind}`)
      .then((d) => { const rows = Array.isArray(d) ? d : d.data || []; setTemplates(rows); onCountChange?.(kind, rows.length); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [kind]); // eslint-disable-line

  const del = async (t) => {
    if (!(await confirmDialog(`Delete the template "${t.name}"? This can't be undone.`))) return;
    try {
      await api.del(`/api/templates/${t.id}`);
      notify("Template deleted", { title: "Deleted", variant: "success" });
      load();
    } catch (e) { notify(e.message, { title: "Error", variant: "error" }); }
  };

  const EMPTY = {
    email: { title: "No email templates yet", subtitle: "Write a reusable message once — merge fields fill in each employee's details when it's used." },
    form: { title: "No custom form templates yet", subtitle: "The forms above are built in. Add a custom form template when you need to collect something they don't cover." },
  }[kind];

  return (
    <div>
      {isForm && (
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <Sec icon={FileText} title="Custom form templates" />
        </div>
      )}
      {loading ? (
        <Cd><div className="text-center py-10 text-t3 text-[14px]">Loading…</div></Cd>
      ) : templates.length === 0 ? (
        <Cd><EmptyState icon={KIND_ICON[kind] || FileText} title={EMPTY.title} subtitle={EMPTY.subtitle} ctaLabel={`Add ${kindLabel}`} onCta={() => setEditing({})} /></Cd>
      ) : (
        <Tbl
          cols={[
            { k: "name", l: "Name", wrap: true, r: (t) => (
              <button onClick={() => setEditing(t)} className="bg-transparent border-0 p-0 cursor-pointer text-left text-[14px] font-bold text-p hover:underline">{t.name}</button>
            ) },
            ...(kind === "email" ? [{ k: "subject", l: "Subject", wrap: true, r: (t) => <span className="text-t2">{t.subject || "—"}</span> }] : []),
            { k: "form", l: "Record type", r: (t) => <Bg c={K.bl} sm>{forms.find((f) => f.key === t.form_key)?.label || t.form_key}</Bg> },
            { k: "cc", l: "Default CC", r: (t) => <span className="text-t3">{(t.default_cc || []).length ? `${t.default_cc.length} address${t.default_cc.length > 1 ? "es" : ""}` : "—"}</span> },
            { k: "updated", l: "Updated", r: (t) => <span className="text-t3">{fmtDate(t.updated_at || t.created_at)}</span> },
            { k: "act", l: "", r: (t) => (
              <div className="flex items-center gap-1 justify-end">
                <Bt v="ghost" s="xs" onClick={() => setEditing(t)}>Edit</Bt>
                <Bt v="ghost" s="xs" icon={Trash2} onClick={() => del(t)} />
              </div>
            ) },
          ]}
          data={templates}
        />
      )}

      {editing !== null && (
        <TemplateEditDialog
          template={editing.id ? editing : null}
          kind={kind}
          forms={forms}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

export const TemplatesPage = () => {
  const [kind, setKind] = useState("email");
  const [forms, setForms] = useState([]);
  const [counts, setCounts] = useState({});
  // How many review forms exist, for the KPI strip — from the same endpoint
  // the catalogue uses, so it can't go stale against the server.
  const [formCount, setFormCount] = useState(null);
  useEffect(() => { loadForms().then((f) => setFormCount(f.length)).catch(() => setFormCount(null)); }, []);
  const [editing, setEditing] = useState(null);
  const kindLabel = TEMPLATE_KINDS.find((k) => k.v === kind)?.l || "Template";

  useEffect(() => { api.get("/api/templates/meta").then((d) => setForms(d.forms || [])).catch(console.error); }, []);
  const onCountChange = (k, n) => setCounts((prev) => (prev[k] === n ? prev : { ...prev, [k]: n }));

  return (
    <div>
      <div className="flex justify-between items-center gap-3 flex-wrap mb-[14px]">
        <h1 className="m-0 text-2xl font-extrabold text-t0">Email/Form Templates</h1>
        <Bt s="sm" icon={Plus} onClick={() => setEditing({})}>Add {kindLabel}</Bt>
      </div>
      <div className="text-[14px] text-t2 mb-[14px]">
        Reusable email content, and the review forms this HRMS runs.
      </div>

      <KpiStrip items={[
        { icon: Mail, label: "Email templates", value: counts.email ?? "—", c: K.p, sub: "reusable messages" },
        { icon: ClipboardList, label: "Form templates", value: counts.form ?? "—", c: K.pu, sub: "custom forms" },
        { icon: Layers, label: "Forms in use", value: formCount ?? "—", c: K.g, sub: "built in" },
      ]} />

      {/* Horizontal tabs — same bar as Appraisal/Employees. */}
      <div className="flex gap-0 mb-4 border-b border-b0 flex-wrap whitespace-nowrap">
        {TEMPLATE_KINDS.map((k) => {
          const Icon = KIND_ICON[k.v] || FileText;
          const active = kind === k.v;
          return (
            <button
              key={k.v}
              onClick={() => setKind(k.v)}
              className={`inline-flex items-center gap-[6px] px-4 py-[7px] bg-transparent border-0 text-[14px] font-semibold cursor-pointer ${active ? "border-b-2 border-p text-t0" : "border-b-2 border-transparent text-t3 hover:text-t1"}`}
            >
              <Icon size={13} color={active ? K.p : K.t3} />
              {k.l}
              {counts[k.v] > 0 && <span className="text-[12px] text-t3">({counts[k.v]})</span>}
            </button>
          );
        })}
      </div>

      {forms.length === 0 ? (
        <Cd><div className="text-center py-10 text-t3 text-[14px]">Loading…</div></Cd>
      ) : (
        <>
          {kind === "form" && <FormsCatalogue />}
          <TemplatesLibraryPanel
            kind={kind}
            forms={forms}
            editing={editing}
            setEditing={setEditing}
            onCountChange={onCountChange}
          />
        </>
      )}
    </div>
  );
};
